import { useTranspilerImportParams } from '@common/utils/hooks/params';
import { useEnabledTranspilers } from '@pages/Team/hooks/useEnabledTranspilers';
import { BackEndCodeLanguage } from '@prophecy/graphql-types/enums';
import { useGlobalVariables } from 'frontend/core/src/context/globalConfig/securedConfigHook';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { ENABLE_TRANSPILER_LANGUAGE } from '../../../utils/localstorage-keys';
import { BasicFormType, FileFormats } from '../config';
import { useTranspilerEnabledLanguages, useTypeSpecificLanguage } from '../hooks';
import { useSupportedSources } from '../useSupportedSources';
import { UseBasicFormTypes } from './types';
import { getDefaultTranspiler } from './utils';

export const isLanguageSelectionEnabled = (transpilerType: string) => {
  return (
    localStorage.getItem(ENABLE_TRANSPILER_LANGUAGE) === 'true' ||
    transpilerType === FileFormats.abinitio ||
    transpilerType === FileFormats.alteryx ||
    transpilerType === FileFormats.datastage ||
    transpilerType === FileFormats.informatica ||
    transpilerType === FileFormats.informaticaCloud
  );
};

export function useBasicFormTypes({ isReimport }: { isReimport: boolean }): UseBasicFormTypes {
  const { searchParams } = useTranspilerImportParams();
  const reImportLanguage = searchParams.language;
  let transpilerType = searchParams.source || '';
  const [recommendedPsetPath, setRecommendedPath] = useState('');
  const enabledTranspilers = useEnabledTranspilers();
  const { isSapEnabled } = useGlobalVariables();

  const formContext = useForm<BasicFormType>({
    mode: 'all',
    defaultValues: {
      projectId: '',
      teamId: '',
      branch: '',
      source: transpilerType,
      fabricId: '',
      airflowFabricId: '',
      clusterSize: '',
      ...searchParams,
      convertLookupsToJoin: Boolean(searchParams.convertLookupsToJoin),
      isJobTranspile: Boolean(searchParams.isJobTranspile),
      isLanguageEnabled: isLanguageSelectionEnabled(transpilerType),
      language: (reImportLanguage as BackEndCodeLanguage) || BackEndCodeLanguage.scala
    }
  });

  transpilerType = formContext.watch('source') || transpilerType;
  const supportedSources = useSupportedSources();
  const supportedLanguages = useTranspilerEnabledLanguages(transpilerType as FileFormats);
  const getTypeSpecificLanguage = useTypeSpecificLanguage();

  useEffect(() => {
    // during reimport we honour searchParams.source even if transpiler is disabled
    // in non import case we honour searchParams.source only if that transpiler is enabled
    let source: FileFormats = '' as FileFormats;
    let isLanguageEnabled: boolean = false;
    let language: BackEndCodeLanguage = BackEndCodeLanguage.python;
    if ((isReimport && transpilerType) || (transpilerType && enabledTranspilers[transpilerType])) {
      source = transpilerType as FileFormats;
      isLanguageEnabled = isLanguageSelectionEnabled(transpilerType);
      language = getTypeSpecificLanguage(transpilerType as FileFormats, supportedLanguages);
    } else {
      let defaultTranspiler = getDefaultTranspiler({ isSapEnabled });
      source = enabledTranspilers[defaultTranspiler] ? defaultTranspiler : ('' as FileFormats);
      isLanguageEnabled = isLanguageSelectionEnabled(source);
      language = getTypeSpecificLanguage(source as FileFormats, supportedLanguages);
      if (!source) {
        source = Object.keys(enabledTranspilers).find(
          (transpiler) =>
            enabledTranspilers[transpiler] === true && supportedSources.includes(transpiler as FileFormats)
        ) as FileFormats;
        isLanguageEnabled = isLanguageSelectionEnabled(source);
        language = getTypeSpecificLanguage(source as FileFormats, supportedLanguages);
      }
    }
    formContext.setValue('source', source || '');
    formContext.setValue('isLanguageEnabled', isLanguageEnabled);
    formContext.setValue('language', language || '');
  }, [
    enabledTranspilers,
    formContext,
    isReimport,
    transpilerType,
    supportedSources,
    isSapEnabled,
    getTypeSpecificLanguage,
    supportedLanguages
  ]);

  return { formContext, recommendedPsetPath, setRecommendedPath };
}
